import {
  collection,
  doc,
  getDoc,
  getDocs,
  query,
  where,
  writeBatch,
  updateDoc,
  setDoc,
} from 'firebase/firestore';
import { firestore } from '../../../lib/firebase/config';
import { COLLECTIONS } from '../constants';
import { Dispute } from '../../../types/dispute';

export const createDispute = async (
  existingLink: any,
  newGuideId: string,
  pilgrimId: string
): Promise<string> => {
  try {
    // First check if there's an existing dispute for this pilgrim
    const disputesRef = collection(firestore, COLLECTIONS.DISPUTES);
    const disputeQuery = query(
      disputesRef,
      where('pilgrimId', '==', pilgrimId),
      where('status', 'in', ['pending', 'under_processing'])
    );
    const disputeSnapshot = await getDocs(disputeQuery);

    // If a dispute already exists, add the new guide to it
    if (!disputeSnapshot.empty) {
      const existingDispute = disputeSnapshot.docs[0];
      const disputeData = existingDispute.data() as Dispute;

      // Get new guide's data
      const newGuideRef = doc(firestore, COLLECTIONS.GUIDES, newGuideId);
      const newGuideDoc = await getDoc(newGuideRef);

      if (!newGuideDoc.exists()) {
        throw new Error('المرشد الجديد غير موجود');
      }

      const newGuideData = newGuideDoc.data();
      const newGuideOrganizerId = newGuideData.organizerId;

      // Get organizer data
      const newOrganizerRef = doc(firestore, COLLECTIONS.ORGANIZERS, newGuideOrganizerId);
      const newOrganizerDoc = await getDoc(newOrganizerRef);

      if (!newOrganizerDoc.exists()) {
        throw new Error('منظم المرشد الجديد غير موجود');
      }

      const newOrganizerData = newOrganizerDoc.data();

      // Check if guide is already part of the dispute
      const isGuideInDispute = disputeData.disputingGuides.some(
        guide => guide.guideId === newGuideId
      );

      if (isGuideInDispute) {
        throw new Error('المرشد مشارك بالفعل في هذا النزاع');
      }

      // Add new guide to the dispute
      const updatedGuides = [
        ...disputeData.disputingGuides,
        {
          guideId: newGuideId,
          guideName: newGuideData.name,
          organizerName: newOrganizerData.name,
          submittedAt: Date.now(),
          evidence: null,
        }
      ];

      // Update the dispute
      await updateDoc(existingDispute.ref, {
        disputingGuides: updatedGuides,
        updatedAt: Date.now()
      });

      return existingDispute.id;
    }

    // If no existing dispute, create a new one
    // Get pilgrim details
    const pilgrimRef = doc(firestore, COLLECTIONS.PILGRIMS, pilgrimId);
    const pilgrimSnap = await getDoc(pilgrimRef);

    if (!pilgrimSnap.exists()) {
      throw new Error('الحاج غير موجود في قاعدة البيانات');
    }

    const pilgrimData = pilgrimSnap.data();

    // Get both guides' data
    const existingGuideRef = doc(firestore, COLLECTIONS.GUIDES, existingLink.guideId);
    const newGuideRef = doc(firestore, COLLECTIONS.GUIDES, newGuideId);
    
    const [existingGuideDoc, newGuideDoc] = await Promise.all([
      getDoc(existingGuideRef),
      getDoc(newGuideRef)
    ]);

    if (!existingGuideDoc.exists() || !newGuideDoc.exists()) {
      throw new Error('أحد المرشدين غير موجود');
    }

    const existingGuideData = existingGuideDoc.data();
    const newGuideData = newGuideDoc.data();

    // Get organizer data for both guides
    const existingOrganizerRef = doc(firestore, COLLECTIONS.ORGANIZERS, existingGuideData.organizerId);
    const newOrganizerRef = doc(firestore, COLLECTIONS.ORGANIZERS, newGuideData.organizerId);

    const [existingOrganizerDoc, newOrganizerDoc] = await Promise.all([
      getDoc(existingOrganizerRef),
      getDoc(newOrganizerRef)
    ]);

    if (!existingOrganizerDoc.exists() || !newOrganizerDoc.exists()) {
      throw new Error('أحد المنظمين غير موجود');
    }

    const existingOrganizerData = existingOrganizerDoc.data();
    const newOrganizerData = newOrganizerDoc.data();

    // Create new dispute document
    const disputeRef = doc(collection(firestore, COLLECTIONS.DISPUTES));
    const disputeData: Dispute = {
      id: disputeRef.id,
      pilgrimId,
      pilgrimName: pilgrimData.Name,
      passportNumber: pilgrimData.Passport_Number,
      pilgrimEmail: pilgrimData.Email || '',
      pilgrimPhone: pilgrimData.Mobile_Number || '',
      pilgrimCountry: pilgrimData.Residence_Country || '',
      packageName: pilgrimData.package_nickName || '', // Add package name
      disputingGuides: [
        {
          guideId: existingLink.guideId,
          guideName: existingGuideData.name,
          organizerName: existingOrganizerData.name,
          submittedAt: existingLink.createdAt,
          evidence: null,
        },
        {
          guideId: newGuideId,
          guideName: newGuideData.name,
          organizerName: newOrganizerData.name,
          submittedAt: Date.now(),
          evidence: null,
        },
      ],
      status: 'pending',
      createdAt: Date.now(),
      updatedAt: Date.now(),
    };

    await setDoc(disputeRef, disputeData);
    return disputeRef.id;
  } catch (error) {
    console.error('Error creating/updating dispute:', error);
    throw error;
  }
};

export const resolveDispute = async (
  disputeId: string,
  resolvedGuideId: string
) => {
  try {
    const batch = writeBatch(firestore);

    // Get dispute details
    const disputeRef = doc(firestore, COLLECTIONS.DISPUTES, disputeId);
    const disputeSnap = await getDoc(disputeRef);

    if (!disputeSnap.exists()) {
      throw new Error('النزاع غير موجود');
    }

    const dispute = disputeSnap.data() as Dispute;
    const now = Date.now();

    // Get winning guide data
    const winningGuide = dispute.disputingGuides.find(g => g.guideId === resolvedGuideId);
    if (!winningGuide) throw new Error('الدليل الفائز غير موجود في النزاع');
    
    // Get guide data to get organizer info
    const guideRef = doc(firestore, 'Guides', resolvedGuideId);
    const guideDoc = await getDoc(guideRef);
    if (!guideDoc.exists()) throw new Error('الدليل الفائز غير موجود');
    
    const guideData = guideDoc.data();
    const organizerId = guideData.organizerId;
    
    // Get organizer data
    const organizerRef = doc(firestore, 'Organizers', organizerId);
    const organizerDoc = await getDoc(organizerRef);
    const organizerName = organizerDoc.exists() ? organizerDoc.data().name : 'Unknown';

    // Create bans for all losing guides
    const losingGuides = dispute.disputingGuides.filter(
      (g) => g.guideId !== resolvedGuideId
    );

    // Create bans for all losing guides
    for (const losingGuide of losingGuides) {
      const banRef = doc(collection(firestore, COLLECTIONS.GUIDE_BANS));
      batch.set(banRef, {
        guideId: losingGuide.guideId,
        pilgrimId: dispute.pilgrimId,
        disputeId,
        createdAt: now
      });
    }

    // Update PilgrimGuideLinks
    const linksRef = collection(firestore, COLLECTIONS.LINKS);
    const linksQuery = query(
      linksRef,
      where('pilgrimId', '==', dispute.pilgrimId)
    );
    const linksSnapshot = await getDocs(linksQuery);

    linksSnapshot.forEach((doc) => {
      const linkData = doc.data();
      if (linkData.guideId === resolvedGuideId) {
        // Approve the winning guide's link
        batch.update(doc.ref, {
          status: 'approved',
          disputeId: null,
          updatedAt: now,
        });
      } else {
        // Delete other guides' links
        batch.delete(doc.ref);
      }
    });

    // Update ownership record
    const ownershipRef = collection(firestore, 'PilgrimOwnership');
    const ownershipQuery = query(
      ownershipRef,
      where('pilgrimId', '==', dispute.pilgrimId)
    );
    const ownershipSnapshot = await getDocs(ownershipQuery);

    if (!ownershipSnapshot.empty) {
      // Update existing ownership record
      const ownershipDoc = ownershipSnapshot.docs[0];
      batch.update(ownershipDoc.ref, {
        realGuideId: resolvedGuideId,
        ownerGuideId: resolvedGuideId,
        updatedAt: now,
      });
    } else {
      // Create new ownership record
      const newOwnershipRef = doc(collection(firestore, 'PilgrimOwnership'));
      batch.set(newOwnershipRef, {
        pilgrimId: dispute.pilgrimId,
        realGuideId: resolvedGuideId,
        ownerGuideId: resolvedGuideId,
        createdAt: now,
        updatedAt: now,
      });
    }
    
    // Update PilgrimSelector
    const selectorRef = collection(firestore, 'PilgrimSelector');
    const selectorQuery = query(selectorRef, where('pilgrimId', '==', dispute.pilgrimId));
    const selectorSnapshot = await getDocs(selectorQuery);
    
    if (!selectorSnapshot.empty) {
      const selectorDoc = selectorSnapshot.docs[0];
      batch.update(selectorDoc.ref, {
        guideId: resolvedGuideId,
        guideName: winningGuide.guideName,
        organizerId,
        organizerName,
        'guideData.name': winningGuide.guideName,
        'guideData.organizerId': organizerId,
        'organizerData.name': organizerName,
        status: 'active',
        updatedAt: now
      });
    }
    
    // Update operationlist entries
    const operationRef = collection(firestore, 'operationlist');
    const operationQuery = query(operationRef, where('pilgrimId', '==', dispute.pilgrimId));
    const operationSnapshot = await getDocs(operationQuery);
    
    if (!operationSnapshot.empty) {
      operationSnapshot.docs.forEach(doc => {
        const opData = doc.data();
        // Preserve existing operatorId, operatorName, etc.
        const operatorId = opData.operatorId;
        const operatorName = opData.operatorName;
        const operatorOrganizerId = opData.operatorOrganizerId;
        const operatorOrganizerName = opData.operatorOrganizerName;
        
        // Update with winning guide's info, but keep operator info
        batch.update(doc.ref, {
          guideId: resolvedGuideId,
          guideName: winningGuide.guideName,
          organizerId,
          organizerName,
          // Preserve operator fields if they exist
          operatorId,
          operatorName,
          operatorOrganizerId,
          operatorOrganizerName,
          updatedAt: now
        });
      });
    }

    // Update dispute status
    batch.update(disputeRef, {
      status: 'resolved',
      resolvedGuideId,
      resolvedAt: now,
      updatedAt: now,
    });

    await batch.commit();
  } catch (error) {
    console.error('Error resolving dispute:', error);
    throw error;
  }
};